import { useMemo } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { Button } from "@project/components/ui/button";
import { cn } from "@project/lib/utils";
import {
  formatJainTithi,
  formatPakshaTithiLine,
  formatPanchangDateKey,
} from "@project/lib/jain-panchang-utils";
import type { JainPanchang } from "@project/types/jain-panchang";

type PanchangCalendarProps = {
  month: Date;
  onMonthChange: (month: Date) => void;
  entries: JainPanchang[];
  loading?: boolean;
  onSelectEntry: (entry: JainPanchang) => void;
  labels: {
    today: string;
    weekdays: string[];
    loading: string;
    vow: string;
  };
};

export function PanchangCalendar({
  month,
  onMonthChange,
  entries,
  loading,
  onSelectEntry,
  labels,
}: PanchangCalendarProps) {
  const entryMap = useMemo(() => {
    const map = new Map<string, JainPanchang>();
    entries.forEach(entry => map.set(entry.english_date, entry));
    return map;
  }, [entries]);

  const days = useMemo(() => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-4 sm:px-6">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          <h2 className="font-serif text-xl text-foreground">
            {format(month, "MMMM yyyy")}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => onMonthChange(subMonths(month, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-4"
            onClick={() => onMonthChange(new Date())}
          >
            {labels.today}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => onMonthChange(addMonths(month, 1))}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="bg-secondary/30 grid grid-cols-7 border-b border-border">
        {labels.weekdays.map((day, index) => (
          <div
            key={day}
            className={cn(
              "px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide",
              index === 0 ? "text-destructive" : "text-muted-foreground"
            )}
          >
            {day}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="px-4 py-16 text-center text-sm text-muted-foreground">
          {labels.loading}
        </div>
      ) : (
        <div key={month.toISOString()} className="anim-fade grid grid-cols-7">
          {days.map(day => {
            const key = formatPanchangDateKey(day);
            const entry = entryMap.get(key);
            const inMonth = isSameMonth(day, month);
            const today = isToday(day);
            const highlighted = Boolean(entry?.highlight_in_calendar);
            const isImportant = Boolean(entry?.is_important);
            const occasion =
              entry?.religious_occasion ??
              entry?.festival_name ??
              formatJainTithi(entry?.jain_tithi);

            return (
              <button
                key={key}
                type="button"
                disabled={!entry}
                onClick={() => entry && onSelectEntry(entry)}
                className={cn(
                  "relative min-h-[5.5rem] border-b border-r border-border p-2 text-left transition sm:min-h-[6.5rem]",
                  !inMonth && "bg-muted/20 text-muted-foreground/60",
                  inMonth && isImportant && "bg-emerald-50",
                  entry &&
                    (isImportant
                      ? "cursor-pointer hover:bg-emerald-100/70"
                      : "hover:bg-gold/5 cursor-pointer"),
                  !entry && "cursor-default"
                )}
              >
                <div className="flex items-start justify-between gap-1">
                  <span
                    className={cn(
                      "inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold",
                      today && "bg-primary text-primary-foreground",
                      !today && inMonth && "text-foreground",
                      !inMonth && "text-muted-foreground/70"
                    )}
                  >
                    {format(day, "d")}
                  </span>
                  {entry && highlighted ? (
                    <span className="bg-gold/15 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-gold">
                      <Sparkles className="h-4 w-4" />
                      {/* {labels.vow} */}
                    </span>
                  ) : null}
                </div>

                {entry ? (
                  <div className="mt-2 space-y-0.5">
                    <p
                      className={cn(
                        "text-[11px] font-medium leading-tight",
                        highlighted ? "text-gold" : "text-foreground"
                      )}
                    >
                      {formatPakshaTithiLine(entry.paksha, entry.jain_tithi)}
                    </p>
                    {occasion ? (
                      <p
                        className={cn(
                          "line-clamp-2 text-[11px] leading-tight",
                          highlighted
                            ? "font-medium text-foreground"
                            : "text-muted-foreground"
                        )}
                      >
                        {occasion}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
